import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Top-down only: loads the catalog plant card beside the schematic when a plant is selected.
// plantSelected / plantSelectionCleared are handled in the capture phase on this.element so we
// still receive them when they are dispatched from inside <turbo-frame id="garden_view"> (bubble
// phase can be stopped at the frame boundary in some Turbo configurations).
export default class extends Controller {
  private readonly onViewChanged = (_e: Event) => {
    this.syncPanelForCurrentView()
  }

  private readonly onFrameLoad = (e: Event) => {
    const t = e.target as HTMLElement
    if (t?.id === "garden_view") this.syncPanelForCurrentView()
  }

  private readonly onPlantSelectedCapture = (e: Event) => {
    if (!(e instanceof CustomEvent)) return
    this.loadCard(e as CustomEvent<{ plantId?: number }>)
  }

  private readonly onPlantSelectionClearedCapture = (e: Event) => {
    if (!(e instanceof CustomEvent)) return
    this.clearPanel()
  }

  override connect() {
    document.addEventListener("viewChanged", this.onViewChanged)
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.element.addEventListener("plantSelected", this.onPlantSelectedCapture, true)
    this.element.addEventListener("plantSelectionCleared", this.onPlantSelectionClearedCapture, true)
    this.syncPanelForCurrentView()
  }

  override disconnect() {
    document.removeEventListener("viewChanged", this.onViewChanged)
    document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    this.element.removeEventListener("plantSelected", this.onPlantSelectedCapture, true)
    this.element.removeEventListener("plantSelectionCleared", this.onPlantSelectionClearedCapture, true)
  }

  /** Map / head-on / grid: hide aside. Top-down: no card until the user selects a plant. */
  syncPanelForCurrentView() {
    const frame = document.getElementById("garden_selected_plant")
    const inTopDown =
      this.element.querySelector("#garden_view .top-down-view-container") !== null

    if (!inTopDown) {
      this.element.classList.remove("garden__body--topdown-active", "garden__body--plant-card-visible")
      if (frame) frame.innerHTML = ""
      return
    }

    this.element.classList.add("garden__body--topdown-active")
    this.element.classList.remove("garden__body--plant-card-visible")
    if (frame) frame.innerHTML = ""
  }

  loadCard(event: CustomEvent<{ plantId?: number }>) {
    const inTopDown =
      this.element.querySelector("#garden_view .top-down-view-container") !== null
    if (!inTopDown) return

    const plantId = event.detail?.plantId
    if (!plantId || plantId <= 0) return

    this.element.classList.add("garden__body--topdown-active")
    this.element.classList.add("garden__body--plant-card-visible")
    Turbo.visit(`/plants/${plantId}/card`, { frame: "garden_selected_plant" })
  }

  clearPanel() {
    const frame = document.getElementById("garden_selected_plant")
    if (frame) frame.innerHTML = ""
    this.element.classList.remove("garden__body--plant-card-visible")
  }

  closeCard() {
    this.clearPanel()
    this.element.dispatchEvent(new CustomEvent("gardenPlantCardClose", { bubbles: true }))
  }
}
